import PropTypes from 'prop-types';
import React, { Component } from 'react';
import NavBar from '../../components/NavBar.jsx';
import NewTeamModal from './components/NewTeamModal.jsx';
import { connect } from 'react-redux';
import { getUsableChannels } from 'selectors/channels/index.js';
import {
  GECKO_CONSTS,
  GeckoCardTable,
  GeckoModalService,
  GeckoDialogService,
  GeckoToastService,
} from '../../ui';
import GeckoDate from 'services/gecko-date';
import GeckoLocation from '../../services/gecko-location';
import GeckoPermissions from '../../services/gecko-permissions';
import GeckoRequest from 'services/gecko-request';
import GeckoPagination from 'services/gecko-pagination';
import './Teams.scss';
import { renderError } from 'services/gecko-error/index.js';
import GeckoFeatures from 'services/gecko-features';

class Teams extends Component {
  navLinks = [
    {
      to: '/agents',
      activeClassName: 'active',
      linkText: 'Agents',
    },
    {
      to: '/teams',
      activeClassName: 'active',
      linkText: 'Teams',
    },
  ];

  cols = [
    {
      key: 'name',
      name: 'Name',
      type: GECKO_CONSTS.TABLE_CELL_TYPE_NAME,
      width: '52%',
    },
    {
      name: 'Status',
      key: 'status',
      align: 'left',
      type: GECKO_CONSTS.TABLE_CELL_TYPE_STATUS,
      styles: [
        {
          name: 'Online',
          type: GECKO_CONSTS.STATUS_TYPE_SUCCESS,
          key: 'Available',
        },
        {
          name: 'Unavailable',
          type: GECKO_CONSTS.STATUS_TYPE_WARNING,
          key: 'Unavailable',
        },
        {
          name: 'Offline',
          type: GECKO_CONSTS.STATUS_TYPE_DEFAULT,
          key: 'Offline',
        },
      ],
    },
    {
      key: 'members',
      name: 'Members',
      width: '10%',
    },
    {
      key: 'last_seen',
      name: 'Last Seen',
      width: '32%',
      render: (value) => {
        return value ? new GeckoDate(value).moment.fromNow() : 'Never';
      },
    },
    this.getDropdown(),
  ];

  featureToggledCols = GeckoFeatures.can('team-channels')
    ? [
        {
          key: 'status',
          align: 'left',
          type: GECKO_CONSTS.TABLE_CELL_TYPE_STATUS,
          styles: [
            {
              name: 'Available',
              type: GECKO_CONSTS.STATUS_TYPE_SUCCESS,
              key: 'Available',
            },
            {
              name: 'Unavailable',
              type: GECKO_CONSTS.STATUS_TYPE_WARNING,
              key: 'Unavailable',
            },
            {
              name: 'Offline',
              type: GECKO_CONSTS.STATUS_TYPE_DEFAULT,
              key: 'Offline',
            },
          ],
        },
        {
          key: 'name',
          name: 'Name',
          type: GECKO_CONSTS.TABLE_CELL_TYPE_NAME,
          width: '22%',
        },
        {
          key: 'members',
          name: 'Members',
          width: '10%',
        },
        {
          key: 'channels_assigned_to',
          name: 'Channels Assigned To',
          width: '32%',
        },
        {
          key: 'last_seen',
          name: 'Last Seen',
          width: '32%',
          render: (value) => {
            return value ? new GeckoDate(value).moment.fromNow() : 'Never';
          },
        },
        this.getDropdown(),
      ]
    : this.cols;

  buildChannelsString(names, maxLength) {
    // This function builds the channel Assigned to string
    // show as many channels as will fit for the maxLength per breakpoint
    // putting the rest in a +x more format
    const numberOfChannelNames = names.length;
    if (names.length === 0) {
      return '';
    }

    let outputString = names.join(', ');

    if (outputString.length <= maxLength) {
      return outputString;
    }

    let hiddenCount = 0;
    while (names.length > 1) {
      names.pop();
      outputString = names.join(', ');
      hiddenCount++;
      outputString += ` + ${hiddenCount} more`;

      if (outputString.length <= maxLength) {
        return outputString;
      }
    }

    return `${numberOfChannelNames} channel${
      numberOfChannelNames > 1 ? 's' : ''
    }`;
  }

  getMaxChannelTextLengthPerBreakpoint = () => {
    switch (this.props.screenSize) {
      case 'xl':
        return 70;
      case 'lg':
        return 40;
      case 'md':
        return 30;
      default:
        return 10;
    }
  };

  getChannelNames(teamId) {
    const channels = this.props.channels
      .filter((c) => {
        return c.assigned_team_ids?.includes(teamId);
      })
      .map((c) => c.name);

    return this.buildChannelsString(
      channels,
      this.getMaxChannelTextLengthPerBreakpoint()
    );
  }

  getDropdown() {
    if (GeckoPermissions.memberOf('agent')) {
      return {};
    }
    return {
      type: GECKO_CONSTS.TABLE_CELL_TYPE_DROPDOWN,
      items: [
        {
          name: 'Edit',
          handler: (row) => this.rowHandler(row),
        },
        {
          name: 'Remove',
          handler: (row) => this.removeTeam(row),
        },
      ],
    };
  }

  returnBtns() {
    if (GeckoPermissions.memberOf('agent')) {
      return [];
    }
    return [
      {
        preset: GECKO_CONSTS.BTN_PRESET_ADD,
        name: 'Add New',
        handler: () => {
          new GeckoModalService().open(() => {
            return (
              <NewTeamModal
                teamNamesList={this.props.teams.map((team) => team.name)}
              />
            );
          });
        },
      },
    ];
  }

  rowHandler(team) {
    return GeckoLocation.goTo(`/teams/${team.id}`);
  }

  removeTeam(team) {
    return new GeckoDialogService()
      .confirm(`Are you sure you want to remove ${team.name}?`)
      .then((confirm) => {
        if (confirm) {
          GeckoRequest.delete(`/models/teams/${team.id}`)
            .then(() => {
              new GeckoToastService().success('Team Removed');
              GeckoLocation.goTo('/teams');
            })
            .catch((err) => {
              renderError(err);
            });
        }
      });
  }

  getAgentCrossPlatformStatus(agent) {
    if (agent.presence === 'Online') {
      return agent.web_available || agent.mobile_available
        ? 'Available'
        : 'Unavailable';
    } else {
      return 'Offline';
    }
  }

  getDetails(team) {
    const memberIDs = this.props.agent_teams
      .filter((registration) => registration.team_id === team.id)
      .map((registration) => registration.agent_id);
    const members = this.props.agents
      .filter((agent) => memberIDs.indexOf(agent.id) > -1)
      .map((agent) => {
        return {
          status: this.getAgentCrossPlatformStatus(agent),
          last_seen: agent.last_active,
        };
      });
    let teamStatus;
    if (members.map((member) => member.status).indexOf('Available') > -1) {
      teamStatus = 'Available';
    } else if (
      members.map((member) => member.status).indexOf('Unavailable') > -1
    ) {
      teamStatus = 'Unavailable';
    } else {
      teamStatus = 'Offline';
    }
    return Object.assign(
      {},
      {
        id: team.id,
        name: team.name,
        status: teamStatus,
        channels_assigned_to: this.getChannelNames(team.id),
        members: members.length,
        last_seen: this.getLastSeen(members),
      }
    );
  }

  getLastSeen(members) {
    const noNulls = members
      .map((status) => status.last_seen)
      .filter((lastSeen) => lastSeen !== null);
    if (noNulls.length === 0) {
      return null;
    }
    return noNulls.sort((a, b) => Date.parse(a) < Date.parse(b))[0];
  }

  compareTeams(a, b) {
    if (
      a.status === 'Available' &&
      (b.status === 'Unavailable' ||
        b.status === 'Offline' ||
        b.status === null)
    ) {
      return -1;
    }
    if (
      a.status === 'Unavailable' &&
      (b.status === 'Offline' || b.status === null)
    ) {
      return -1;
    }
    if (
      (a.status === 'Unavailable' ||
        a.status === 'Offline' ||
        a.status === null) &&
      b.status === 'Available'
    ) {
      return 1;
    }
    if (
      (a.status === 'Offline' || b.status === null) &&
      b.status === 'Unavailable'
    ) {
      return 1;
    } else {
      if (a.name.toUpperCase() < b.name.toUpperCase()) {
        return -1;
      }
      if (a.name.toUpperCase() > b.name.toUpperCase()) {
        return 1;
      }
      return 0;
    }
  }

  render() {
    const rows = this.props.teams
      .map((team) => this.getDetails(team))
      .sort((a, b) => this.compareTeams(a, b));
    const [teams, pagination] = GeckoPagination.paginate({
      page: this.props.params.page,
    })(rows);
    return (
      <div className="Teams">
        <NavBar navLinks={this.navLinks} />
        <div className="content-margin">
          <GeckoCardTable
            name="Teams"
            cols={this.featureToggledCols}
            rows={teams}
            headerBtns={this.returnBtns()}
            rowHandler={this.rowHandler.bind(this)}
            pagination={pagination}
            alert="You have not created any Teams yet."
            newLinkHandler={() => {
              new GeckoModalService().open(() => {
                return (
                  <NewTeamModal
                    teamNamesList={this.props.teams.map((team) => team.name)}
                  />
                );
              });
            }}
          />
        </div>
      </div>
    );
  }
}

Teams.defaultProps = {
  teams: [],
  pagination: {},
};

Teams.propTypes = {
  teams: PropTypes.array,
  pagination: PropTypes.object,
};

const mapStateToProps = (state) => {
  return {
    params: state.location.params,
    teams: state.teams.map((team) => {
      return {
        id: team.id,
        name: team.name,
      };
    }),
    agent_teams: state.agent_teams,
    agents: state.agents,
    channels: getUsableChannels(state),
    screenSize: state.appState.screenSize,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {};
};

export default connect(mapStateToProps, mapDispatchToProps)(Teams);
